import { useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Field, SelectField } from '@/components/forms/CompanyFields';
import { supabase } from '@/lib/supabase';

const SUBJECTS = [
  { value: 'duvida', label: 'Dúvida sobre a plataforma' },
  { value: 'publicidade', label: 'Publicidade e planos' },
  { value: 'moderacao', label: 'Moderação e denúncias' },
  { value: 'imprensa', label: 'Imprensa' },
];

const EMPTY = { name: '', email: '', subject: '', message: '', website: '' };

async function sendContact(payload) {
  const { data, error } = await supabase.functions.invoke('send-contact', { body: payload });
  if (error) {
    let msg = 'Não foi possível enviar sua mensagem agora. Tente novamente em instantes.';
    try {
      const j = await error.context.json();
      if (j?.error) msg = j.error;
    } catch (e) {
      // sem corpo de erro
    }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.name.trim().length < 2) er.name = 'Informe seu nome.';
    if (!/^\S+@\S+\.\S+$/.test(f.email)) er.email = 'Informe um e-mail válido.';
    if (!f.subject) er.subject = 'Selecione o assunto.';
    if (f.message.trim().length < 15) er.message = 'Escreva ao menos 15 caracteres.';
    setErrors(er);
    if (Object.keys(er).length) return;

    setBusy(true);
    try {
      await sendContact({
        name: f.name.trim(),
        email: f.email.trim(),
        subject: SUBJECTS.find((s) => s.value === f.subject)?.label || 'Contato',
        message: f.message.trim(),
        website: f.website,
      });
      setF(EMPTY);
      setErrors({});
      toast.success('Mensagem enviada', { description: 'Responderemos pelo e-mail informado.' });
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="surface relative grid gap-4 p-5 sm:grid-cols-2" data-testid="platform-contact-form">
      {/* Campo invisível para pessoas: robôs costumam preenchê-lo, e o servidor descarta esses envios */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor="pc-website">Site</label>
        <input id="pc-website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} />
      </div>

      <Field id="pc-name" label="Nome" required error={errors.name}>
        <Input id="pc-name" value={f.name} onChange={set('name')} data-testid="pc-name" />
      </Field>
      <Field id="pc-email" label="E-mail" required error={errors.email}>
        <Input id="pc-email" type="email" value={f.email} onChange={set('email')} data-testid="pc-email" />
      </Field>
      <Field id="pc-subject" label="Assunto" required error={errors.subject} className="sm:col-span-2">
        <SelectField id="pc-subject" value={f.subject} onChange={set('subject')} placeholder="Selecione" options={SUBJECTS} />
      </Field>
      <Field id="pc-message" label="Mensagem" required error={errors.message} className="sm:col-span-2">
        <Textarea id="pc-message" rows={5} value={f.message} onChange={set('message')} data-testid="pc-message" />
      </Field>
      <div className="sm:col-span-2 flex justify-end">
        <Button type="submit" className="btn-glow" disabled={busy} data-testid="pc-submit">
          {busy ? <Loader2 className="animate-spin" /> : <Send />} Enviar
        </Button>
      </div>
    </form>
  );
};